﻿import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Download } from "lucide-react";

import useListController from "@/hooks/list/useListController";
import { useHistoriesLookups, useHistories, useRestoreInventory } from "@/hooks/histories/useHistories";
import { useHistoryExport } from "@/hooks/histories/useHistoryExport";
import useFilterStore from "@/stores/useFilterStore";

import ListScaffold from "@/components/desktop/list/table/ListScaffold";
import CustomConfirmModal from "@/components/desktop/modal/CustomConfirmModal";

import { useOtherMovementsHistoryFilters } from "./hooks/useOtherMovementsHistoryFilters";
import { useOtherMovementsHistoryColumns } from "./hooks/useOtherMovementsHistoryColumns";
import { OTHER_MOVEMENTS_FILTERS, OTHER_MOVEMENTS_METADATA, OTHER_MOVEMENTS_BASE_TABLE } from "./other-movements-history.constants";
import { getHistoryQuickActions } from "../Home/histories-metadata";

const OtherMovementsHistory = () => {
	const navigate = useNavigate();
	const { filter } = useFilterStore();
	const { search, estate_parent_id, estate_id } = filter;

	const [showRestoreModal, setShowRestoreModal] = useState(false);
	const [selectedInventoryId, setSelectedInventoryId] = useState(null);

	const {
		isFilterOpen,
		filtersState,
		appliedFiltersState,
		tableState,
		appliedFilterParams,
		reactTableSort,
		openFilter,
		closeFilter,
		handleFilterChange,
		handleApplyFilters,
		handleClearAllFilters,
		handleClearOneFilter,
		handlePageChange,
		handlePageSizeChange,
		handleSortByChange,
		setFilterOptions,
	} = useListController({
		baseFilter: OTHER_MOVEMENTS_FILTERS,
		baseTable: OTHER_MOVEMENTS_BASE_TABLE,
	});

	useEffect(() => {
		handlePageChange(1);
	}, [search]); // eslint-disable-line react-hooks/exhaustive-deps

	const { data: lookups, isLoading: isLoadingLookups } = useHistoriesLookups({
		type: "other_movements",
	});

	const { isReady } = useOtherMovementsHistoryFilters({
		lookups,
		filtersState,
		setFilterOptions,
		handleFilterChange,
		handleApplyFilters,
	});

	const { data, isLoading, isFetching } = useHistories(
		OTHER_MOVEMENTS_METADATA.apiType,
		{
			...appliedFilterParams,
			...tableState,
			search,
			estate_parent_id,
			estate_id,
		},
		{
			enabled: !isLoadingLookups && Boolean(lookups) && isReady,
		},
	);

	const { mutate: restoreInventory, isLoading: isRestoring } = useRestoreInventory({
		onSuccess: () => {
			setShowRestoreModal(false);
			setSelectedInventoryId(null);
		},
		onError: () => {
			setShowRestoreModal(false);
			setSelectedInventoryId(null);
		},
	});

	const handleRestoreRequest = (inventoryId) => {
		setSelectedInventoryId(inventoryId);
		setShowRestoreModal(true);
	};

	const handleRestoreConfirm = () => {
		if (selectedInventoryId != null) {
			restoreInventory({ inventoryId: selectedInventoryId });
		}
	};

	const columns = useOtherMovementsHistoryColumns({ onRestore: handleRestoreRequest });

	const quickActions = useMemo(() => {
		const actions = getHistoryQuickActions("otros_movimientos");
		return actions.map((action) => ({
			...action,
			onClick: () => navigate(action.path),
		}));
	}, [navigate]);

	const { handleExport, isAlreadyExporting } = useHistoryExport("other_movements", appliedFilterParams);

	const exportButton = useMemo(
		() => [
			{
				key: "export",
				label: "Exportar",
				icon: Download,
				variant: "secondary",
				disabled: isAlreadyExporting,
				options: [
					{ key: "excel", label: "Otros movimientos", onClick: () => handleExport("") },
					{ key: "kardex", label: "Kardex", onClick: () => handleExport("kardex") },
					{ key: "global", label: "Global consolidado", onClick: () => handleExport("global") },
				],
			},
		],
		[handleExport, isAlreadyExporting],
	);

	return (
		<>
			<ListScaffold
				quickActions={quickActions}
				actionButtons={exportButton}
				subtitle={OTHER_MOVEMENTS_METADATA.subtitle}
				filterTitle={`Filtrar ${OTHER_MOVEMENTS_METADATA.title}`}
				isFilterOpen={isFilterOpen}
				filters={filtersState}
				appliedFilters={appliedFiltersState}
				onFilterChange={handleFilterChange}
				onApplyFilters={handleApplyFilters}
				onClearFilters={handleClearAllFilters}
				onClearOneFilter={handleClearOneFilter}
				onOpenFilter={openFilter}
				onCloseFilter={closeFilter}
				isFetching={isFetching}
				isLoading={isLoading || isLoadingLookups}
				data={data?.data || []}
				totalItems={data?.totalRecords || 0}
				totalPages={data?.totalPages || 0}
				toItem={data?.data?.length || 0}
				fromItem={data?.page === 1 ? 1 : (data?.page - 1) * tableState.per_page + 1}
				pageIndex={tableState.page}
				columns={columns}
				onPageChange={handlePageChange}
				pageSize={tableState.per_page}
				onPageSizeChange={handlePageSizeChange}
				sortBy={reactTableSort}
				onSortByChange={handleSortByChange}
				pageSizeOptions={[20, 50, 100, 200]}
				initialPageSize={tableState.per_page}
				initialSortBy={reactTableSort}
				compactHeaders={true}
				compactBody={true}
			/>
			<CustomConfirmModal
				open={showRestoreModal}
				onClose={() => setShowRestoreModal(false)}
				onConfirm={handleRestoreConfirm}
				title="Restaurar inventario"
				message="¿Está seguro que desea restaurar este inventario? Se recuperarán las aplicaciones asociadas y se creará una nueva orden de compra."
				confirmText="Sí, restaurar"
				cancelText="No, cancelar"
				isLoading={isRestoring}
				processingMessage="Restaurando inventario..."
			/>
		</>
	);
};

export default OtherMovementsHistory;
